// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import { UilExternalLinkAlt, UilPadlock } from '@iconscout/react-unicons';
import React from 'react';
import { Link } from 'react-navi';

import { FEATURE_FLAG } from 'lib/featureFlags';
import { Spinner } from '../../../../components/Spinner';
import {
  useAuthService,
  useCertificationDownload,
  useCertificationViewedActivity,
  useFeatureFlag,
  useRequestAccessRedirectionUrl,
} from '../../../../lib/state';
import { downloadFileFromAPI, shouldHideItem } from '../../../../lib/utils';
import * as Styles from './DashboardDocuments.styles';
import { GlideButton } from '../../../../components/GlideButton';

const ACTION_ICON_SIZE = 20;

function CertificationActionButton({
  link,
  canDownload,
  canView,
  fileName,
  documentId,
  isDownloading,
  setDownloading,
  setShouldShowDownloadErrorToast,
}) {
  const { currentTeam, authenticatedUser } = useAuthService();
  const { requestAccessRedirectionUrl } = useRequestAccessRedirectionUrl();
  const { trackCertificationView } = useCertificationViewedActivity();
  const downloadCertification = useCertificationDownload(currentTeam.id, documentId);
  const isRequestAccessHidden = useFeatureFlag(FEATURE_FLAG.HIDE_REQUEST_ACCESS);

  const shouldHideRequestAccessButton = shouldHideItem(isRequestAccessHidden, [
    canDownload,
    canView,
    authenticatedUser,
  ]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFileFromAPI(() => downloadCertification(), fileName);
    } catch (e) {
      setShouldShowDownloadErrorToast(true);
    } finally {
      setDownloading(false);
    }
  };

  const handleCertificationClick = () => {
    trackCertificationView(documentId);
    window.open(link);
  };

  if (canDownload) {
    return (
      <GlideButton
        variant="secondary"
        icon={isDownloading ? <Spinner /> : <Styles.DownloadIcon size={ACTION_ICON_SIZE} />}
        onClick={() => handleDownload()}
        disabled={isDownloading}
        className="secondary"
      >
        Download
      </GlideButton>
    );
  }

  if (canView && !canDownload) {
    return (
      <Styles.LinkButton
        icon={<UilExternalLinkAlt size={ACTION_ICON_SIZE} />}
        onClick={handleCertificationClick}
        target="_blank"
        variant="secondary"
        className="secondary"
      >
        View
      </Styles.LinkButton>
    );
  }

  if (!shouldHideRequestAccessButton) {
    return (
      <Styles.LinkButton
        as={Link}
        variant="secondary"
        icon={<UilPadlock size={ACTION_ICON_SIZE} />}
        href={requestAccessRedirectionUrl}
        className="secondary"
      >
        Request Access
      </Styles.LinkButton>
    );
  }
  return null;
}

export default CertificationActionButton;
